import { studio } from "@/data/site";

// Edit legal copy here. Placeholders are marked with [ ].
export const legal = {
  privacy: {
    title: "Privacy Policy",
    updated: "June 2026",
    intro: `This policy explains how ${studio.name} handles information in our apps and on this website. It applies to every app listed on this site unless that app publishes its own policy — in which case the app-specific policy is linked from its page and takes precedence.`,
    appNote:
      "App-specific note: where an app collects something different from the general policy below, it is listed in the “App-specific handling” section.",
    sections: [
      {
        title: "Who we are",
        body: [
          `${studio.legalName} ("we", "us") is an independent mobile app studio operating ${studio.domain}.`,
          `Contact for any privacy question or request: ${studio.supportEmail}.`,
          "[Add your business address and, if required in your jurisdiction, a data-protection contact.]",
        ],
      },
      {
        title: "Information we collect",
        body: [
          "Information you enter in the app — for example cycle dates, symptoms, notes or preferences. This is what makes the app useful to you.",
          "Information you send us — your name, email address and message when you contact support.",
          "Basic technical information — device type, operating system version and crash/diagnostic data, used to fix problems.",
          "[If you add analytics, advertising or a third-party login, list them here with the data each one receives.]",
        ],
      },
      {
        title: "How we use it",
        body: [
          "To provide the features of the app you are using.",
          "To answer your support messages.",
          "To diagnose crashes and improve stability.",
          "We do not sell personal information, and we do not use your health entries for advertising.",
        ],
      },
      {
        title: "Legal bases (where applicable)",
        body: [
          "Where GDPR or a similar law applies, we rely on your consent for optional features, and on legitimate interest for security and stability.",
          "[Confirm the bases that apply to your business with a qualified adviser.]",
        ],
      },
      {
        title: "Storage and retention",
        body: [
          "[State where data is stored — on device only, and/or with which hosting provider and region.]",
          "[State how long you keep support messages and account data.]",
          "You can ask us to delete your data at any time using the contact details above.",
        ],
      },
      {
        title: "Sharing",
        body: [
          "We share information only with service providers needed to run the app (for example hosting or crash reporting), and only what they need.",
          "[List your current providers here.]",
          "We may disclose information if legally required to do so.",
        ],
      },
      {
        title: "Your rights",
        body: [
          "Depending on where you live, you may have the right to access, correct, export or delete your data, and to withdraw consent.",
          `To exercise any of these, email ${studio.supportEmail}. We will confirm once your request is handled.`,
        ],
      },
      {
        title: "Children",
        body: [
          "Our apps are not directed at children under [age]. We do not knowingly collect data from them.",
        ],
      },
      {
        title: "App-specific handling",
        body: [
          "AuraHealth: entries you log about your cycle and symptoms are health-related information. It is used to show your own history and educational context inside the app. It is not sold and not used for advertising.",
          "[Add app-specific notes for future apps here, or publish a separate policy and link it from that app's page.]",
        ],
      },
      {
        title: "Changes",
        body: [
          "If this policy changes we will update the date at the top and, where the change is significant, notify you in the app.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    updated: "June 2026",
    intro: `These terms apply to this website and to the apps published by ${studio.name}. By using them you agree to what is set out below. If you do not agree, please stop using them.`,
    appNote:
      "App store note: your use of an app is also subject to the terms of the store you downloaded it from (Apple App Store or Google Play).",
    sections: [
      {
        title: "Licence",
        body: [
          "We grant you a personal, non-exclusive, revocable licence to use our apps for their intended purpose on devices you own or control.",
          "You may not copy, resell, reverse engineer or attempt to extract source code from our apps, except where the law allows it.",
        ],
      },
      {
        title: "Acceptable use",
        body: [
          "Do not use our apps or website unlawfully, to disrupt the service, to attempt unauthorised access, or to upload content you do not have the right to share.",
        ],
      },
      {
        title: "No medical, legal or professional advice",
        body: [
          "Content in our apps and on this site is informational and educational only. It is not medical advice, diagnosis or treatment, and it is not a substitute for a qualified professional.",
          "Never delay seeking professional advice because of something you read in one of our apps.",
        ],
      },
      {
        title: "Accounts",
        body: [
          "If an app offers an account, you are responsible for keeping your credentials secure and for activity under your account.",
        ],
      },
      {
        title: "Purchases and subscriptions",
        body: [
          "Paid features may be offered as one-off purchases or subscriptions. Billing, renewals, refunds and cancellations are handled by Apple or Google under their terms.",
          "Cancel a subscription in your App Store or Google Play account settings. We cannot cancel or refund store purchases on your behalf.",
        ],
      },
      {
        title: "Availability and changes",
        body: [
          "We may add, change or remove features, and may suspend a service for maintenance. We may discontinue an app; where reasonable we will give notice.",
        ],
      },
      {
        title: "Intellectual property",
        body: [
          `The name, logos, design and code of our apps and this site belong to ${studio.legalName}, except where third-party material is used under licence.`,
          "Content you create in an app remains yours.",
        ],
      },
      {
        title: "Disclaimer and liability",
        body: [
          "Our apps are provided “as is”. We do not warrant that they will be error-free or uninterrupted.",
          "To the extent permitted by law, we are not liable for indirect or consequential loss. Nothing in these terms limits liability that cannot be limited by law.",
          "[Have a qualified adviser review the liability wording for your jurisdiction.]",
        ],
      },
      {
        title: "Governing law",
        body: [
          "These terms are governed by the laws of [your country / state]. Disputes will be handled by the courts of [jurisdiction].",
        ],
      },
      {
        title: "Contact",
        body: [`Questions about these terms: ${studio.supportEmail}.`],
      },
    ],
  },
};
